import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import Button from '../shared/Button';
import { useAuthContext } from '../AuthProvider';
import { useSubscription } from '../../hooks/useSubscription';
import { resolveExpandedAccessState } from '../../utils/expandedAccess';

function dismissKey(userId) {
	return `wf_expanded_access_upgrade_prompt_${userId || 'anon'}`;
}

/**
 * After the 7-day Essentials expanded-access window, ask the user to upgrade.
 * Shows on each new browser session (and again next day if dismissed).
 */
export default function ExpandedAccessUpgradeModal() {
	const { user, isAuthenticated } = useAuthContext();
	const { subscription, getEffectivePlanName, isLoading, isPlanVerified } = useSubscription();
	const [open, setOpen] = useState(false);

	useEffect(() => {
		// isPlanVerified: never upsell off an unverified plan (failed fetch defaults
		// to essentials and would wrongly prompt paying users during backend outages)
		if (!isAuthenticated || isLoading || !isPlanVerified) {
			setOpen(false);
			return;
		}
		const plan = getEffectivePlanName();
		if (plan === 'essayist' || plan === 'laureate') {
			setOpen(false);
			return;
		}
		const { expired } = resolveExpandedAccessState(subscription, user?.createdAt);
		if (!expired) {
			setOpen(false);
			return;
		}

		const key = dismissKey(user.id || user._id);
		try {
			const raw = localStorage.getItem(key);
			if (raw) {
				const dismissedAt = Number(raw);
				// Hide for 24h after dismiss; show again next day / next login cycle
				if (Number.isFinite(dismissedAt) && Date.now() - dismissedAt < 24 * 60 * 60 * 1000) {
					setOpen(false);
					return;
				}
			}
		} catch {
			/* ignore */
		}
		setOpen(true);
	}, [
		isAuthenticated,
		isLoading,
		isPlanVerified,
		subscription,
		user?.createdAt,
		user?.id,
		user?._id,
		getEffectivePlanName
	]);

	const dismiss = () => {
		const key = dismissKey(user?.id || user?._id);
		try {
			localStorage.setItem(key, String(Date.now()));
		} catch {
			/* ignore */
		}
		setOpen(false);
	};

	if (!open) return null;

	return (
		<div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
			<div className="absolute inset-0 bg-black/70" onClick={dismiss} />
			<div className="relative z-10 w-full max-w-[480px] rounded-[18px] bg-white shadow-2xl p-7">
				<button
					type="button"
					aria-label="Close"
					className="absolute right-4 top-4 text-gray-400 hover:text-gray-700"
					onClick={dismiss}
				>
					<X className="w-5 h-5" />
				</button>
				<h2 className="text-[22px] font-bold text-dark-blue leading-snug pr-8">
					Your 7-day expanded access has ended
				</h2>
				<p className="mt-3 text-sm text-gray leading-relaxed">
					You&apos;re now on the free Essentials plan with standard limits. Upgrade to Essayist or
					Laureate for more documents, sources, and document types.
				</p>
				<div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
					<button
						type="button"
						onClick={dismiss}
						className="inline-flex min-w-[110px] items-center justify-center rounded-[10px] border border-lightGrey px-4 py-2.5 text-sm font-medium text-dark-blue hover:bg-[#F7F7FB]"
					>
						Continue on Essentials
					</button>
					<Link to="/subscription" onClick={dismiss}>
						<Button
							variant="primary"
							className="inline-flex min-w-[140px] items-center justify-center rounded-[10px] px-4 py-2.5 text-sm font-semibold !w-auto"
						>
							Upgrade now
						</Button>
					</Link>
				</div>
			</div>
		</div>
	);
}
